import { type CallLog } from '@/types/CallLog'
import { firestoreService } from '@/utils/firestoreService'
import { CallLogModel } from '@/models/CallLogModel'
import { useCallLogStore } from '@/stores/callLogStore'
import { useTimelineActivityStore } from '@/stores/timelineActivityStore'

const COLLECTION = 'call_logs'

/**
 * CallLog Service - Provides business logic for call log operations
 * Uses firestore for persistence and the call log store for state management
 */
export function useCallLogService() {
  const callLogStore = useCallLogStore()

  return {
    /**
     * Add a new call log with validation
     */
    async addCallLog(data: Partial<CallLog>): Promise<CallLog> {
      const callLog = CallLogModel.create(data)

      if (!CallLogModel.validate(callLog)) {
        throw new Error('Invalid call log data')
      }
      await addTimelineActivity(callLog)
      return await callLogStore.add(callLog)
    },

    /**
     * Load call logs for a specific person
     */
    async loadForPerson(personId: string): Promise<CallLog[]> {
      await callLogStore.load()
      return callLogStore.items.filter((log: CallLog) => log.personId === personId)
    },

    /**
     * Load call logs for a specific organization
     */
    async loadForOrganization(organizationId: string): Promise<CallLog[]> {
      await callLogStore.load()
      return callLogStore.items.filter((log: CallLog) => log.organizationId === organizationId)
    },

    /**
     * Delete a call log by ID
     */
    async deleteCallLog(id: string): Promise<void> {
      await firestoreService.delete(COLLECTION, id)
      callLogStore.remove(id)
    },

    /**
     * Get call logs with specific tags
     */
    async getCallLogsByTags(tagIds: string[]): Promise<CallLog[]> {
      await callLogStore.load()
      return callLogStore.items.filter((log: CallLog) =>
        log.tags?.some((tagId: string) => tagIds.includes(tagId)),
      )
    },

    /**
     * Add tags to a call log
     */
    async addTagsToCallLog(callLogId: string, tagIds: string[]): Promise<void> {
      const callLog = await callLogStore.get(callLogId)
      if (!callLog) {
        throw new Error('Call log not found')
      }

      const updatedTags = [...new Set([...(callLog.tags || []), ...tagIds])]
      await callLogStore.update(callLogId, {
        tags: updatedTags,
        updatedAt: new Date(),
      })
    },
  }
}

// Re-export the direct Firestore methods for backward compatibility
export const callLogService = {
  async add(log: CallLog) {
    return firestoreService.add<CallLog>(COLLECTION, log)
  },
  async listByPerson(personId: string) {
    return firestoreService.query<CallLog>(COLLECTION, [
      { field: 'personId', op: '==', value: personId },
    ])
  },
  async listByOrganization(organizationId: string) {
    return firestoreService.query<CallLog>(COLLECTION, [
      { field: 'organizationId', op: '==', value: organizationId },
    ])
  },
  async delete(id: string) {
    return firestoreService.delete(COLLECTION, id)
  },
}

/**
 * Adds an entry to the timeline activity store for a call log
 */
async function addTimelineActivity(callLog: CallLog): Promise<void> {
  const timelineStore = useTimelineActivityStore()

  await timelineStore.add({
    type: 'call',
    title: `${callLog.direction === 'incoming' ? 'Incoming' : 'Outgoing'} call`,
    description: callLog.notes,
    createdAt: callLog.timestamp,
    personId: callLog.personId,
    organizationId: callLog.organizationId,
    entityId: callLog.id!,
    entityType: 'call_logs',
    tags: callLog.tags,
  })
}
